'use client';

import React, { useState, useMemo } from 'react';
import { Calculator, Sparkles, TrendingUp, ShieldCheck } from 'lucide-react';

export default function BioeconomyROICalculator() {
  const [compounds, setCompounds] = useState(100000);
  const [wetCost, setWetCost] = useState(50);
  const [aiCost, setAiCost] = useState(25000);
  const [successRate, setSuccessRate] = useState(90);

  const stats = useMemo(() => {
    const rawWetLabCost = compounds * wetCost;
    // Dry-lab filters out 95% of candidates before wet-lab validation
    const dryLabCandidates = Math.ceil(compounds * 0.05);
    const validationCost = dryLabCandidates * wetCost;
    const totalDryLabCost = aiCost + validationCost;
    const netSavings = rawWetLabCost - totalDryLabCost;
    const roi = (netSavings / totalDryLabCost) * 100;
    
    // Estimate timeline compression based on size
    const timeSavedMonths = Math.min(36, Math.max(6, Math.ceil(compounds / 20000)));

    return {
      rawWetLabCost,
      totalDryLabCost,
      netSavings,
      roi,
      timeSavedMonths
    };
  }, [compounds, wetCost, aiCost]);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };

  return (
    <div className='bg-card border-border my-8 rounded-2xl border p-6 md:p-8 shadow-xl space-y-6'>
      <div className='flex items-center gap-3 border-b border-border/40 pb-4'>
        <div className='flex size-10 items-center justify-center rounded-xl bg-brand-dark/10 dark:bg-brand-dark-text-primary/10'>
          <Calculator className='size-5 text-brand-dark dark:text-brand-dark-text-primary' />
        </div>
        <div>
          <h4 className='text-foreground text-xl font-serif font-black tracking-tight'>
            Dry-Lab Economics & ROI Simulator
          </h4>
          <p className='text-muted-foreground text-xs font-medium'>
            Evaluate the cost-benefit analysis of computational screening vs. raw wet-lab pipeline assays.
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
        {/* Sliders Input Panel */}
        <div className='lg:col-span-6 space-y-6 bg-muted/10 border border-border/20 p-5 rounded-xl'>
          {/* Compounds Slider */}
          <div className='space-y-2'>
            <div className='flex justify-between text-xs font-black uppercase text-muted-foreground tracking-wide'>
              <span>Compounds to Screen</span>
              <span className='text-foreground font-mono font-bold'>{compounds.toLocaleString()} molecules</span>
            </div>
            <input
              type='range'
              min='10000'
              max='1000000'
              step='10000'
              value={compounds}
              onChange={(e) => setCompounds(Number(e.target.value))}
              className='w-full accent-emerald-600 cursor-pointer h-1.5 bg-muted/40 rounded-lg appearance-none'
            />
          </div>

          {/* Wet-Lab Assay Cost */}
          <div className='space-y-2'>
            <div className='flex justify-between text-xs font-black uppercase text-muted-foreground tracking-wide'>
              <span>Wet-Lab Cost Per Assay</span>
              <span className='text-foreground font-mono font-bold'>{wetCost} USD / screen</span>
            </div>
            <input
              type='range'
              min='5'
              max='200'
              step='5'
              value={wetCost}
              onChange={(e) => setWetCost(Number(e.target.value))}
              className='w-full accent-emerald-600 cursor-pointer h-1.5 bg-muted/40 rounded-lg appearance-none'
            />
          </div>

          {/* AI Compute & Setup Cost */}
          <div className='space-y-2'>
            <div className='flex justify-between text-xs font-black uppercase text-muted-foreground tracking-wide'>
              <span>AI Compute & Pipelines</span>
              <span className='text-foreground font-mono font-bold'>{formatCurrency(aiCost)} flat rate</span>
            </div>
            <input
              type='range'
              min='5000'
              max='100000'
              step='5000'
              value={aiCost}
              onChange={(e) => setAiCost(Number(e.target.value))}
              className='w-full accent-emerald-600 cursor-pointer h-1.5 bg-muted/40 rounded-lg appearance-none'
            />
          </div>
        </div>

        {/* Results Graph & Analytics */}
        <div className='lg:col-span-6 flex flex-col justify-between space-y-6'>
          <div className='grid grid-cols-2 gap-4'>
            {/* Traditional Cost */}
            <div className='bg-muted/10 border border-border/20 p-4 rounded-xl space-y-1.5'>
              <span className='text-[10px] font-black uppercase text-muted-foreground tracking-widest'>Wet-Lab Only Cost</span>
              <p className='text-foreground text-xl font-mono font-black'>{formatCurrency(stats.rawWetLabCost)}</p>
            </div>

            {/* AI dry-lab Cost */}
            <div className='bg-muted/10 border border-border/20 p-4 rounded-xl space-y-1.5'>
              <span className='text-[10px] font-black uppercase text-muted-foreground tracking-widest'>AI dry-lab Cost</span>
              <p className='text-brand-dark dark:text-brand-dark-text-primary text-xl font-mono font-black'>{formatCurrency(stats.totalDryLabCost)}</p>
            </div>
          </div>

          {/* ROI Metric Callout */}
          <div className='bg-emerald-950/20 dark:bg-emerald-950/10 border border-emerald-500/25 p-5 rounded-xl flex items-center justify-between'>
            <div className='space-y-1'>
              <span className='text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-400 tracking-widest flex items-center gap-1.5'>
                <TrendingUp className='size-3.5' /> Net Capital Savings
              </span>
              <h5 className='text-foreground text-2xl font-mono font-black'>
                {formatCurrency(stats.netSavings)}
              </h5>
            </div>
            <div className='text-right'>
              <span className='text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-400 tracking-widest'>Est. ROI</span>
              <p className='text-emerald-600 dark:text-emerald-400 text-2xl font-mono font-black'>
                +{stats.roi.toFixed(0)}%
              </p>
            </div>
          </div>

          {/* Timeline Speedup */}
          <div className='flex items-center gap-3 bg-indigo-950/20 dark:bg-indigo-950/10 border border-indigo-500/25 p-4 rounded-xl text-sm'>
            <Sparkles className='size-5 text-indigo-500 shrink-0' />
            <span className='text-foreground font-medium'>
              AI screening accelerates clinical target delivery by approximately **{stats.timeSavedMonths} months**.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
